/** Number formatting + relative-time helpers. Ported from the legacy JS. */

export function fmtMoney(n: number | null | undefined, currency = "USD"): string {
  if (n == null) return "";
  const prefix = currency === "USD" ? "$" : `${currency} `;
  return prefix + Number(n).toLocaleString("en-US");
}

export function fmtSalary(
  min: number | null | undefined,
  max: number | null | undefined,
  currency = "USD",
): string {
  if (!min && !max) return "Negotiable";
  if (min && max) return `${fmtMoney(min, currency)} – ${fmtMoney(max, currency)} / mo`;
  if (min) return `from ${fmtMoney(min, currency)} / mo`;
  return `up to ${fmtMoney(max, currency)} / mo`;
}

export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

/** "Just now", "5m ago", "Posted 2h ago", "yesterday", "3d ago", "13 Mar". */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  if (sec < 60) return "just now";
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day === 1) return "yesterday";
  if (day < 14) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function initials(name?: string | null, email?: string | null): string {
  const src = (name || email || "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+|@|\./).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function parseRate(raw: string | number | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw);
  const digits = s.replace(/[^0-9.]/g, "");
  if (!digits) return null;
  let n = Number(digits);
  if (isNaN(n)) return null;
  if (/k/i.test(s) && n < 1000) n = n * 1000;
  return Math.round(n);
}

/** Best-effort: extract numeric min/max from a free-text budget like
 *  "$7,000 – $9,000" or "5k-8k". Used to seed Publish form numeric inputs. */
export function parseBudgetRange(budget: string | null | undefined): {
  min: number | null;
  max: number | null;
} {
  if (!budget) return { min: null, max: null };
  const matches = String(budget).match(/[\d.,]+\s*[kKmM]?/g);
  if (!matches) return { min: null, max: null };
  const ints = matches
    .map((s) => {
      const k = /[kK]/.test(s);
      const m = /[mM]/.test(s);
      const n = parseFloat(s.replace(/[^\d.]/g, ""));
      if (isNaN(n)) return null;
      return Math.round(n * (m ? 1_000_000 : k ? 1_000 : 1));
    })
    .filter((n): n is number => n !== null && n > 0);
  if (ints.length >= 2) return { min: ints[0], max: ints[1] };
  if (ints.length === 1) return { min: null, max: ints[0] };
  return { min: null, max: null };
}

const ENGAGEMENT_LABELS: Record<string, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  project_based: "Project-based",
};

const SENIORITY_LABELS: Record<string, string> = {
  junior: "Junior",
  middle: "Middle",
  senior: "Senior",
  lead: "Lead / Principal",
};

const SERVICE_LABELS: Record<string, string> = {
  outstaffing: "Outstaffing",
  recruiting: "Recruiting",
};

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  in_review: "In review",
  published: "Published",
  forwarded: "Forwarded",
  declined: "Declined",
  active: "Active",
  filled: "Filled",
  closed: "Closed",
};

function titleCase(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function humanize(value: string | null | undefined, kind?: keyof typeof MAPS): string {
  if (!value) return "—";
  const map = kind ? MAPS[kind] : undefined;
  if (map && map[value]) return map[value];
  return titleCase(value);
}

const MAPS = {
  engagement: ENGAGEMENT_LABELS,
  seniority: SENIORITY_LABELS,
  service: SERVICE_LABELS,
  status: STATUS_LABELS,
} as const;
